import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  decideWorkflowRequest,
  fetchSchedulerRequestQueue,
  reopenWorkflowRequest,
  type WorkflowRequestRecord,
  type WorkflowSummary,
} from '../request-workflow/api';

const emptySummary: WorkflowSummary = {
  private: 0,
  pending: 0,
  approved: 0,
  denied: 0,
  cancelled: 0,
  auto_approved: 0,
  modified: 0,
  withdrawn: 0,
};

const statusBadgeClass: Record<WorkflowRequestRecord['queueState'], string> = {
  private: 'bg-secondary-transparent text-secondary',
  pending: 'bg-warning-transparent text-warning',
  approved: 'bg-success-transparent text-success',
  denied: 'bg-danger-transparent text-danger',
  cancelled: 'bg-dark-transparent text-dark',
  auto_approved: 'bg-success-transparent text-success',
  modified: 'bg-info-transparent text-info',
  withdrawn: 'bg-dark-transparent text-dark',
};

const requestTypeLabel: Record<WorkflowRequestRecord['requestType'], string> = {
  pto: 'PTO',
  admin: 'Admin',
  academic: 'Academic',
  specific_shift: 'Request to work specific shifts',
  cannot_work_specific_shift: 'Cannot work specific shifts',
  swap_shift: 'Swap shifts',
  time_off: 'Time off',
  availability: 'Availability',
};

const displayScheduleName = (name: string) => name.replace(/\s+schedule$/i, '').trim();

const formatDateRange = (record: WorkflowRequestRecord) => {
  const start = new Date(record.start);
  const end = new Date(record.end);

  if (record.allDay) {
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  }

  return `${start.toLocaleString()} - ${end.toLocaleString()}`;
};

const RequestQueuePage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedGroupSlug, setSelectedGroupSlug] = useState('');
  const [availableGroups, setAvailableGroups] = useState<Array<{ id: number; name: string; slug: string; company: { slug: string | null; name: string | null } }>>([]);
  const [requests, setRequests] = useState<WorkflowRequestRecord[]>([]);
  const [summary, setSummary] = useState<WorkflowSummary>(emptySummary);
  const [loading, setLoading] = useState(true);
  const [actionState, setActionState] = useState<{ requestId: string; decision: 'approved' | 'denied' | 'reopened' } | null>(null);
  const [notesByRequest, setNotesByRequest] = useState<Record<string, string>>({});
  const [hasLoadedQueue, setHasLoadedQueue] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadQueue = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const params = new URLSearchParams(location.search);
      const payload = await fetchSchedulerRequestQueue(params.get('group') ?? undefined);

      setSelectedGroupSlug(payload.selectedGroup?.slug ?? 'all');
      setAvailableGroups(payload.availableGroups);
      setRequests(payload.requests);
      setSummary(payload.summary);
      setHasLoadedQueue(true);

      const canonicalParams = new URLSearchParams(location.search);
      if (payload.selectedGroup?.company.slug) {
        canonicalParams.set('company', payload.selectedGroup.company.slug);
      } else {
        canonicalParams.delete('company');
      }
      canonicalParams.set('group', payload.selectedGroup?.slug ?? 'all');
      if (canonicalParams.toString() !== new URLSearchParams(location.search).toString()) {
        navigate(
          { pathname: location.pathname, search: `?${canonicalParams.toString()}` },
          { replace: true }
        );
      }
    } catch (requestError) {
      setLoadError(requestError instanceof Error ? requestError.message : 'Unable to load the request queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadQueue();
  }, [location.search]);

  const pendingRequests = useMemo(
    () => requests.filter((record) => record.workflowStatus === 'pending'),
    [requests]
  );

  const updateGroup = (nextGroupId: string) => {
    const params = new URLSearchParams(location.search);
    params.set('group', nextGroupId);
    if (nextGroupId === 'all') params.delete('company');
    navigate({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const handleNoteChange = (requestId: string) =>
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setNotesByRequest((current) => ({
        ...current,
        [requestId]: event.target.value,
      }));
    };

  const handleDecision = async (record: WorkflowRequestRecord, decision: 'approved' | 'denied') => {
    setActionState({ requestId: record.id, decision });
    setActionError(null);
    setSuccess(null);

    try {
      await decideWorkflowRequest(record.group.id, record.id, {
        decision,
        note: notesByRequest[record.id]?.trim() || undefined,
        expected_version: record.version,
      });
      setSuccess(`Request ${decision === 'approved' ? 'approved' : 'denied'} for ${record.requester.name ?? 'member'}.`);
      await loadQueue();
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : 'Unable to save this approval decision.');
    } finally {
      setActionState(null);
    }
  };

  const handleReopen = async (record: WorkflowRequestRecord) => {
    const note = notesByRequest[record.id]?.trim();
    if (!note) {
      setActionError('Explain why this denial is being reopened so the override is auditable.');
      return;
    }
    setActionState({ requestId: record.id, decision: 'reopened' });
    setActionError(null);
    setSuccess(null);
    try {
      await reopenWorkflowRequest(record.group.id, record.id, { note, expected_version: record.version });
      setSuccess(`Denied request reopened for ${record.requester.name ?? 'member'}.`);
      await loadQueue();
    } catch (requestError) {
      setActionError(requestError instanceof Error ? requestError.message : 'Unable to reopen this request.');
    } finally {
      setActionState(null);
    }
  };

  const summaryValue = (value: number) => (hasLoadedQueue ? value : '—');
  const showInitialLoadFailure = !hasLoadedQueue && loadError;
  const showRefreshFailure = hasLoadedQueue && loadError;

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Request Queue" />
        <Alert variant="danger" className="custom-card">
          This scheduler workspace requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to review approval requests.
        </Alert>
      </>
    );
  }

  if (!bootstrap.navigation.schedulerReview) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Access Required" />
        <Alert variant="warning" className="custom-card">
          This review queue is limited to platform admins and scheduler admins for their assigned departments.
        </Alert>
      </>
    );
  }

  return (
    <>
      <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Request Queue" />

      <Card className="custom-card">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-stretch">
            <Col xl={3} lg={4}>
              <div className="border rounded-3 bg-light p-3 h-100">
                <Form.Label className="text-uppercase text-muted fs-12 fw-semibold mb-2">Schedule group</Form.Label>
                <Form.Select
                  value={selectedGroupSlug}
                  onChange={(event) => updateGroup(event.target.value)}
                  disabled={loading || availableGroups.length === 0}
                >
                  <option value="all">All authorized groups</option>
                  {availableGroups.map((group) => (
                    <option key={group.id} value={group.slug}>
                      {displayScheduleName(group.name)}
                    </option>
                  ))}
                </Form.Select>
                <div className="text-muted fs-12 mt-2">Only groups in your assigned scheduling scope are available.</div>
              </div>
            </Col>
            <Col xl={9} lg={8}>
              <Row className="g-2">
                <Col xs={6} md={4}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-2 p-xl-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Pending</p>
                      <h3 className="mb-0">{summaryValue(summary.pending)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-2 p-xl-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Approved</p>
                      <h3 className="mb-0">{summaryValue(summary.approved)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-2 p-xl-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Denied</p>
                      <h3 className="mb-0">{summaryValue(summary.denied)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-2 p-xl-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Awaiting action</p>
                      <h3 className="mb-0">{hasLoadedQueue ? pendingRequests.length : '—'}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-2 p-xl-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Auto-approved</p>
                      <h3 className="mb-0">{summaryValue(summary.auto_approved)}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} md={4}>
                  <Card className="custom-card shadow-none border mb-0">
                    <Card.Body className="p-2 p-xl-3">
                      <p className="text-muted text-uppercase fs-12 fw-semibold mb-1">Modified / withdrawn</p>
                      <h3 className="mb-0">{hasLoadedQueue ? `${summary.modified} / ${summary.withdrawn}` : '—'}</h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {showRefreshFailure ? (
        <Alert variant="warning" className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
          <span>{loadError} Showing the last successfully loaded queue until retry succeeds.</span>
          <Button variant="outline-warning" size="sm" onClick={() => void loadQueue()} disabled={loading}>
            Retry queue load
          </Button>
        </Alert>
      ) : null}
      {actionError ? <Alert variant="warning">{actionError}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Card className="custom-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Requests in scope</Card.Title>
          {loading ? <Spinner animation="border" size="sm" /> : null}
        </Card.Header>
        <Card.Body>
          {loading && !hasLoadedQueue ? (
            <Alert variant="light" className="mb-0" role="status">
              Loading scheduling requests for this department…
            </Alert>
          ) : showInitialLoadFailure ? (
            <Alert variant="warning" className="mb-0 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
              <span>{loadError}</span>
              <Button variant="outline-warning" size="sm" onClick={() => void loadQueue()} disabled={loading}>
                Retry queue load
              </Button>
            </Alert>
          ) : requests.length === 0 ? (
            <Alert variant="light" className="mb-0">
              No scheduling-admin-visible requests are currently queued for this selection.
            </Alert>
          ) : (
            <Stack gap={3}>
              {requests.map((record) => {
                const busy = actionState?.requestId === record.id;

                return (
                  <Card key={record.id} className="border shadow-none mb-0">
                    <Card.Body>
                      <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                        <div>
                          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                            <h4 className="mb-0">{record.title}</h4>
                            <Badge bg="" className={statusBadgeClass[record.queueState]}>
                              {record.queueState.replace('_', ' ')}
                            </Badge>
                            <Badge bg="" className="bg-light text-muted border">
                              {requestTypeLabel[record.requestType]}
                            </Badge>
                          </div>
                          <p className="text-muted mb-0">
                            {record.requester.name} • {record.requester.email}
                          </p>
                        </div>
                        <div className="text-muted text-end">
                          <div>{formatDateRange(record)}</div>
                          <div className="fs-12">{record.group.name}</div>
                        </div>
                      </div>

                      {record.details ? <p className="mb-3">{record.details}</p> : null}

                      {record.workflowStatus === 'pending' ? (
                        <Row className="g-3 align-items-end">
                          <Col xl={8}>
                            <Form.Label className="text-uppercase text-muted fs-12 fw-semibold mb-2">Decision note</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={notesByRequest[record.id] ?? ''}
                              onChange={handleNoteChange(record.id)}
                              placeholder="Optional reason, coverage conditions, or follow-up instructions."
                            />
                          </Col>
                          <Col xl={4}>
                            <div className="d-flex gap-2 justify-content-xl-end">
                              <Button
                                variant="outline-danger"
                                disabled={busy}
                                onClick={() => void handleDecision(record, 'denied')}
                              >
                                {busy && actionState?.decision === 'denied' ? 'Saving...' : 'Deny'}
                              </Button>
                              <Button
                                variant="success"
                                disabled={busy}
                                onClick={() => void handleDecision(record, 'approved')}
                              >
                                {busy && actionState?.decision === 'approved' ? 'Saving...' : 'Approve'}
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      ) : record.workflowStatus === 'denied' ? (
                        <Row className="g-3 align-items-end">
                          <Col xl={8}>
                            <Form.Label className="text-uppercase text-muted fs-12 fw-semibold mb-2">Reopen reason</Form.Label>
                            <Form.Control as="textarea" rows={2} value={notesByRequest[record.id] ?? ''}
                              onChange={handleNoteChange(record.id)} placeholder="Required reason for overriding the denial." />
                          </Col>
                          <Col xl={4} className="text-xl-end">
                            <Button variant="outline-primary" disabled={busy} onClick={() => void handleReopen(record)}>
                              {busy ? 'Reopening...' : 'Reopen request'}
                            </Button>
                          </Col>
                        </Row>
                      ) : (
                        <Alert variant={record.workflowStatus === 'approved' ? 'success' : 'danger'} className="mb-0">
                          <strong>{record.workflowStatus === 'approved' ? 'Approved' : 'Denied'}</strong>
                          {record.decision?.decidedByName ? ` by ${record.decision.decidedByName}` : ''}
                          {record.decision?.note ? `: ${record.decision.note}` : '.'}
                        </Alert>
                      )}
                      {record.lifecycle.length > 0 ? (
                        <details className="mt-3">
                          <summary className="text-muted fs-12 fw-semibold">Lifecycle history ({record.lifecycle.length})</summary>
                          <ol className="mt-2 mb-0 ps-3">
                            {record.lifecycle.map((entry, index) => (
                              <li key={`${entry.version ?? index}-${entry.type}`} className="mb-1">
                                <strong>{entry.type.replace('_', ' ')}</strong> · {new Date(entry.occurred_at).toLocaleString()}
                                {entry.actor_name ? ` by ${entry.actor_name}` : ''}{entry.note ? ` — ${entry.note}` : ''}
                              </li>
                            ))}
                          </ol>
                        </details>
                      ) : null}
                    </Card.Body>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default RequestQueuePage;
